import { useState, useEffect, useRef } from "react";
import type { SiteSettings } from "@/mocks/adminData";
import { useGuaranteeVerify } from "@/hooks/useGuaranteeVerify";

const NAV_ITEMS = [
  { label: "关于合盈", id: "about" },
  { label: "核心价值观", id: "values" },
  { label: "官方商务", id: "business" },
  { label: "担保验证", id: "guarantee" },
  { label: "活动专栏", id: "activity" },
  { label: "服务体系", id: "service" },
  { label: "投诉接受", id: "complaint" },
];

interface TopBarProps {
  settings: SiteSettings;
}

export default function TopBar({ settings }: TopBarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchVal, setSearchVal] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileDropdownRef = useRef<HTMLDivElement>(null);

  const { result: verifyResult, loading: verifying, verify, reset } = useGuaranteeVerify();

  const siteName = settings.site_name || "合盈 663.com";
  const logoImage = settings.logo_image || "https://www.au2p0rz.vip/assets/logo-hx-BTyYeU-t.webp";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const inPc = dropdownRef.current?.contains(target);
      const inMobile = mobileDropdownRef.current?.contains(target);
      if (!inPc && !inMobile) {
        reset();
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [reset]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const handleSearch = () => {
    if (!searchVal.trim()) return;
    verify(searchVal);
  };

  // 验证结果下拉卡片（PC 和手机共用）
  const ResultDropdown = ({ wide }: { wide?: boolean }) =>
    verifyResult ? (
      <div className={`absolute top-full mt-1.5 rounded-xl p-3 text-xs z-50 border ${
        wide ? "right-0 w-72" : "left-0 right-0"
      } ${
        verifyResult.status === "success"
          ? "bg-green-50 border-green-200"
          : "bg-red-50 border-red-200"
      }`}>
        <div className="flex items-start gap-2">
          <i className={`text-sm mt-0.5 flex-shrink-0 ${
            verifyResult.status === "success"
              ? "ri-checkbox-circle-fill text-green-500"
              : "ri-close-circle-fill text-red-500"
          }`}></i>
          <div className="min-w-0 flex-1">
            <p className={`font-semibold leading-snug ${
              verifyResult.status === "success" ? "text-green-700" : "text-red-700"
            }`}>
              {verifyResult.message}
            </p>
            {verifyResult.data && (
              <div className="mt-1.5 space-y-0.5 text-gray-600">
                {verifyResult.data.name && (
                  <p>名称：<span className="font-medium">{verifyResult.data.name}</span></p>
                )}
                {verifyResult.data.remark && (
                  <p>备注：<span className="font-medium">{verifyResult.data.remark}</span></p>
                )}
              </div>
            )}

          </div>
          <button
            onClick={reset}
            className="w-4 h-4 flex items-center justify-center flex-shrink-0 cursor-pointer opacity-50 hover:opacity-100"
          >
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>
      </div>
    ) : null;

  return (
    <header className={`w-full sticky top-0 z-50 transition-all duration-300 ${
      scrolled ? "bg-white border-b border-gray-100" : "bg-white"
    }`}>

      {/* ===== PC端导航 ===== */}
      <div className="hidden md:flex w-full items-center h-14 px-6 lg:px-10 gap-6">
        {/* Logo */}
        <a href="/" className="flex items-center gap-2 flex-shrink-0 cursor-pointer">
          <img
            src={logoImage}
            alt="HX"
            className="w-8 h-8 object-contain"
            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
          />
          <span className="text-sm font-bold text-gray-800 whitespace-nowrap">{siteName}</span>
        </a>

        {/* 导航菜单 */}
        <nav className="flex items-center gap-1 flex-1 overflow-x-auto">
          {NAV_ITEMS.map(item => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="text-xs text-gray-600 hover:text-red-500 px-2.5 py-1.5 rounded-md hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap font-medium"
            >
              {item.label}
            </button>
          ))}
        </nav>

        {/* 搜索验证框 */}
        <div className="relative flex-shrink-0 w-56" ref={dropdownRef}>
          <div className={`flex items-center bg-gray-100 rounded-full px-3 py-1.5 gap-2 transition-all ${
            verifyResult ? "ring-2 ring-red-200" : ""
          }`}>
            <input
              type="text"
              value={searchVal}
              onChange={(e) => { setSearchVal(e.target.value); reset(); }}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder="输入担保ID验证"
              className="flex-1 min-w-0 bg-transparent text-xs text-gray-700 outline-none placeholder-gray-400"
            />
            <button
              onClick={handleSearch}
              disabled={verifying || !searchVal.trim()}
              className="w-5 h-5 flex items-center justify-center flex-shrink-0 cursor-pointer disabled:opacity-40"
            >
              {verifying
                ? <i className="ri-loader-4-line text-gray-400 text-sm animate-spin"></i>
                : <i className="ri-search-line text-gray-400 text-sm"></i>}
            </button>
          </div>
          <ResultDropdown wide />
        </div>

        {/* 国旗 */}
        <div className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full overflow-hidden border-2 border-red-500">
          <span className="text-sm leading-none">🇨🇳</span>
        </div>
      </div>

      {/* ===== 手机端导航 ===== */}
      <div className="flex md:hidden w-full px-3 items-center h-12 gap-2">
        {/* Logo */}
        <a href="/" className="flex items-center gap-1.5 flex-shrink-0 cursor-pointer">
          <img
            src={logoImage}
            alt="HX"
            className="w-7 h-7 object-contain"
            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
          />
          <span className="text-xs font-bold text-gray-800 whitespace-nowrap leading-tight">
            {siteName.length > 8 ? siteName.slice(0, 8) + "…" : siteName}
          </span>
        </a>

        {/* 搜索框 */}
        <div className="flex-1 min-w-0 relative" ref={mobileDropdownRef}>
          <div className={`flex items-center bg-gray-100 rounded-full px-2.5 py-1.5 gap-1.5 transition-all ${
            verifyResult ? "ring-2 ring-red-200" : ""
          }`}>
            <input
              type="text"
              value={searchVal}
              onChange={(e) => { setSearchVal(e.target.value); reset(); }}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder="输入担保ID验证"
              className="flex-1 min-w-0 bg-transparent text-xs text-gray-700 outline-none placeholder-gray-400"
            />
            <button
              onClick={handleSearch}
              disabled={verifying || !searchVal.trim()}
              className="w-5 h-5 flex items-center justify-center flex-shrink-0 cursor-pointer disabled:opacity-40"
            >
              {verifying
                ? <i className="ri-loader-4-line text-gray-400 text-sm animate-spin"></i>
                : <i className="ri-search-line text-gray-400 text-sm"></i>}
            </button>
          </div>
          <ResultDropdown />
        </div>

        {/* 国旗 */}
        <div className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full overflow-hidden border-2 border-red-500">
          <span className="text-sm leading-none">🇨🇳</span>
        </div>

        {/* 菜单按钮 */}
        <button
          className="w-7 h-7 flex-shrink-0 flex items-center justify-center cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-lg text-gray-700`}></i>
        </button>
      </div>

      {/* 手机端下拉菜单 */}
      {menuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-white border-t border-gray-100 z-50">
          <div className="px-4 py-3 grid grid-cols-2 gap-1">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className="text-sm text-gray-700 hover:text-red-500 text-left py-2.5 px-3 rounded-lg hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
